import { useState, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Truck, MapPin, Play, CheckCircle, Loader2, ChevronDown } from 'lucide-react';
import useSWR from 'swr';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../hooks/useAuth';
import { useUIStore } from '../../hooks/useUIStore';
import { useAppointmentActions } from '../../hooks/useAppointmentActions';
import { Appointment } from '../../types/appointments';
import { formatApptLabel } from '../../utils/formatApptLabel';
import { getRankedDockAssignments } from '../../utils/predictiveDockAssignment';
import { extractPOMetadataForScoring } from '../../utils/poMetadataHelpers';
import { formatDockCapabilities } from '../../utils/formatDockCapabilities';
import { LateTimeModal } from '../LateTimeModal';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../ui/alert-dialog';

import { formatDateInTimeZone, formatTimeInTimeZone } from '../../utils/timezoneDisplay';

// Helper to format time
const formatTime = (dateStr: string, timeZone?: string) => {
    return formatTimeInTimeZone(dateStr, timeZone);
};

const getRelativeTime = (dateStr: string) => {
    const diff = new Date(dateStr).getTime() - Date.now();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 0) return `${Math.abs(minutes)}m ago`;
    if (minutes < 60) return `in ${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return `in ${hours}h`;
};

// Row action state machine
type RowAction = 'checkin' | 'dispatch' | 'assign_dock' | 'active' | 'done' | 'waiting';

function getRowAction(status: string, hasDock: boolean): RowAction {
    switch (status) {
        case 'approved':
            return hasDock ? 'checkin' : 'assign_dock';
        case 'checked_in':
            return hasDock ? 'dispatch' : 'assign_dock';
        case 'in_progress':
            return 'active';
        case 'completed':
            return 'done';
        default:
            return 'waiting';
    }
}

// Sort priority: actionable rows first
function getActionPriority(action: RowAction): number {
    switch (action) {
        case 'assign_dock': return 0;
        case 'checkin': return 1;
        case 'dispatch': return 2;
        case 'active': return 3;
        case 'waiting': return 4;
        case 'done': return 5;
        default: return 6;
    }
}

export function CheckInModal() {
    const { isCheckInModalOpen, toggleCheckInModal } = useUIStore();
    const { token, user } = useAuthStore();
    const [searchTerm, setSearchTerm] = useState('');

    // Unified confirmation state for check-in and dispatch
    const [pendingConfirm, setPendingConfirm] = useState<{
        type: 'checkin' | 'dispatch';
        title: string;
        description: string;
        confirmLabel: string;
        confirmClass: string;
        execute: () => void;
    } | null>(null);

    // Inline dock assignment state (keyed by appointment id)
    const [dockPickerOpen, setDockPickerOpen] = useState<string | null>(null);
    const [selectedDockId, setSelectedDockId] = useState('');
    const [assigningDock, setAssigningDock] = useState(false);

    // Row refs for auto-advance scroll
    const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

    const fetcher = (url: string) => fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
    }).then(res => res.json());

    // Fetch today's appointments
    const { data, mutate, isLoading } = useSWR<{ appointments: Appointment[] }>(
        isCheckInModalOpen && token ? '/api/appointments/universal?filter=today' : null,
        fetcher,
        { refreshInterval: 15000 }
    );

    // Fetch docks for inline assignment
    const warehouseId = user?.warehouseId;
    const { data: docksData } = useSWR(
        isCheckInModalOpen && token && warehouseId ? [`/api/warehouses/${warehouseId}/docks`, token] : null,
        fetcher
    );
    const availableDocks = useMemo(() =>
        (docksData?.docks || []).filter((d: any) => {
            const s = (d.status || '').toLowerCase();
            return s !== 'maintenance' && !s.includes('out of service') && !s.includes('out_of_service');
        }),
    [docksData]);

    const appointments = data?.appointments || [];

    // Shared check-in + dispatch via hook
    const { checkIn, dispatchToDock, lateModalProps, isActionLoading } = useAppointmentActions({
        onSuccess: () => mutate(),
    });

    // Filter and sort logic
    const filteredAppointments = useMemo(() => {
        let list = appointments;

        // Search filter
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(a =>
                (a.customerPoNumber?.toLowerCase().includes(term)) ||
                (a.carrierName?.toLowerCase().includes(term)) ||
                (a.driverName?.toLowerCase().includes(term)) ||
                (a.loadNumber?.toLowerCase().includes(term))
            );
        }

        // Sort: actionable rows first, then by scheduled time
        return [...list].sort((a, b) => {
            const statusA = (a.status || '').toLowerCase();
            const statusB = (b.status || '').toLowerCase();
            const hasDockA = !!(a.dockId || a.dock_id);
            const hasDockB = !!(b.dockId || b.dock_id);
            const priorityA = getActionPriority(getRowAction(statusA, hasDockA));
            const priorityB = getActionPriority(getRowAction(statusB, hasDockB));
            if (priorityA !== priorityB) return priorityA - priorityB;
            return new Date(a.startAt).getTime() - new Date(b.startAt).getTime();
        });
    }, [appointments, searchTerm]);

    // --- Auto-advance helpers (must be after filteredAppointments) ---
    const findNextActionableAppointment = useCallback((currentId: string) => {
        const currentIdx = filteredAppointments.findIndex(a => a.id === currentId);
        if (currentIdx === -1) return null;
        const order = [
            ...filteredAppointments.slice(currentIdx + 1),
            ...filteredAppointments.slice(0, currentIdx),
        ];
        for (const a of order) {
            const s = (a.status || '').toLowerCase();
            const hasDock = !!(a.dockId || a.dock_id);
            const action = getRowAction(s, hasDock);
            if (action === 'checkin' || action === 'dispatch' || action === 'assign_dock') {
                return a.id;
            }
        }
        return null;
    }, [filteredAppointments]);

    const focusNextActionableRow = useCallback((currentId: string) => {
        if (!isCheckInModalOpen) return;
        requestAnimationFrame(() => {
            const nextId = findNextActionableAppointment(currentId);
            if (!nextId) return;
            const el = rowRefs.current[nextId];
            if (!el) return;
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('ring-2', 'ring-primary/40');
            setTimeout(() => {
                el.classList.remove('ring-2', 'ring-primary/40');
            }, 600);
        });
    }, [isCheckInModalOpen, findNextActionableAppointment]);

    // Inline dock assignment handler
    const handleAssignDock = useCallback(async (appointmentId: string) => {
        if (!selectedDockId || !token || assigningDock) return;
        setAssigningDock(true);
        try {
            const res = await fetch(`/api/appointments/${appointmentId}/reassign-dock`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ dockId: selectedDockId }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || 'Dock assignment failed');
            }
            toast.success('Dock assigned');
            setDockPickerOpen(null);
            setSelectedDockId('');
            await mutate();
            focusNextActionableRow(appointmentId);
        } catch (err: any) {
            toast.error(err.message || 'Failed to assign dock');
        } finally {
            setAssigningDock(false);
        }
    }, [selectedDockId, token, assigningDock, mutate, focusNextActionableRow]);

    // Counts for header
    const actionableCounts = useMemo(() => {
        let checkinReady = 0;
        let dispatchReady = 0;
        let needsDock = 0;
        appointments.forEach(a => {
            const s = (a.status || '').toLowerCase();
            const hasDock = !!(a.dockId || a.dock_id);
            const action = getRowAction(s, hasDock);
            if (action === 'checkin') checkinReady++;
            else if (action === 'dispatch') dispatchReady++;
            else if (action === 'assign_dock') needsDock++;
        });
        return { checkinReady, dispatchReady, needsDock };
    }, [appointments]);

    const onClose = () => {
        toggleCheckInModal(false);
        setSearchTerm('');
        setDockPickerOpen(null);
        setSelectedDockId('');
    };

    if (!isCheckInModalOpen) return null;

    return (
        <AnimatePresence>
            {isCheckInModalOpen && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    />

                    {/* Modal Window */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="relative w-full max-w-3xl bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-slate-200 dark:border-primary/50 rounded-2xl shadow-[0_0_50px_rgba(124,58,237,0.2)] flex flex-col max-h-[85vh]"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 border-b border-white/10 bg-white/5 shrink-0">
                            <div>
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white font-outfit flex items-center gap-2">
                                    <Truck className="text-primary" />
                                    Gate Workflow
                                </h2>
                                <div className="flex items-center gap-3 mt-1">
                                    {actionableCounts.checkinReady > 0 && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 font-bold">
                                            {actionableCounts.checkinReady} check-in
                                        </span>
                                    )}
                                    {actionableCounts.dispatchReady > 0 && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-400 font-bold">
                                            {actionableCounts.dispatchReady} dispatch
                                        </span>
                                    )}
                                    {actionableCounts.needsDock > 0 && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 font-bold">
                                            {actionableCounts.needsDock} need dock
                                        </span>
                                    )}
                                    {actionableCounts.checkinReady === 0 && actionableCounts.dispatchReady === 0 && actionableCounts.needsDock === 0 && (
                                        <span className="text-xs text-slate-500">No pending actions</span>
                                    )}
                                </div>
                            </div>
                            <button onClick={onClose} className="text-slate-400 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        {/* Search Bar */}
                        <div className="p-4 border-b border-slate-200/50 dark:border-white/10 bg-slate-50/50 dark:bg-black/20">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={16} />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search by PO, Load #, Carrier, or Driver name..."
                                    className="w-full bg-white dark:bg-black/40 border border-slate-200 dark:border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-slate-900 dark:text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all text-sm placeholder:text-slate-400 dark:placeholder:text-slate-600"
                                    autoFocus
                                />
                            </div>
                        </div>

                        {/* Content */}
                        <div className="overflow-y-auto p-0 custom-scrollbar flex-1">
                            {isLoading ? (
                                <div className="p-8 flex justify-center">
                                    <span className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></span>
                                </div>
                            ) : filteredAppointments.length === 0 ? (
                                <div className="flex flex-col items-center justify-center p-12 text-center">
                                    <div className="bg-slate-100 dark:bg-white/5 p-4 rounded-full mb-4">
                                        <Truck className="text-slate-400 dark:text-slate-600" size={32} />
                                    </div>
                                    <h3 className="text-slate-700 dark:text-slate-300 font-bold mb-1">No appointments found</h3>
                                    <p className="text-slate-500 text-sm max-w-xs">
                                        {searchTerm ? 'Try adjusting your search terms.' : 'There are no scheduled appointments for today.'}
                                    </p>
                                </div>
                            ) : (
                                <div className="divide-y divide-slate-100 dark:divide-white/5">
                                    {filteredAppointments.map((appt) => {
                                        const status = (appt.status || '').toLowerCase();
                                        const hasDock = !!(appt.dockId || appt.dock_id);
                                        const rowAction = getRowAction(status, hasDock);
                                        const loading = isActionLoading(appt.id) || (assigningDock && dockPickerOpen === appt.id);
                                        const isDockPickerShown = dockPickerOpen === appt.id;

                                        // Find dock number for display
                                        const dockId = appt.dockId || appt.dock_id;
                                        const dockObj = dockId ? availableDocks.find((d: any) => d.id === dockId) : null;
                                        const dockLabel = dockObj?.dockNumber || (dockId ? 'Dock' : null);

                                        return (
                                            <div key={appt.id} ref={(el) => { rowRefs.current[appt.id] = el; }} className="p-4 hover:bg-slate-50 dark:hover:bg-white/5 transition-all group rounded-lg">
                                                <div className="flex items-center justify-between gap-4">
                                                    <div className="flex items-center gap-4 flex-1 min-w-0">
                                                        {/* Time column */}
                                                        <div className="flex flex-col items-center min-w-[60px] text-center">
                                                            <span className="text-lg font-bold text-slate-900 dark:text-white font-mono">{formatTime(appt.startAt, user?.timeZone)}</span>
                                                            <span className="text-[10px] uppercase text-slate-400 dark:text-slate-500 font-bold">{getRelativeTime(appt.startAt)}</span>
                                                        </div>

                                                        <div className="h-8 w-px bg-slate-200 dark:bg-white/10"></div>

                                                        {/* Info column */}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-0.5">
                                                                <span className="font-bold text-slate-900 dark:text-white truncate text-sm">{formatApptLabel(appt)}</span>
                                                                {/* Status pill */}
                                                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full uppercase font-bold 
                                                                    ${status === 'approved' ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400' :
                                                                        status === 'checked_in' ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' :
                                                                            status === 'in_progress' ? 'bg-violet-500/20 text-violet-600 dark:text-violet-400' :
                                                                                status === 'completed' ? 'bg-slate-200 dark:bg-slate-500/20 text-slate-500 dark:text-slate-400' :
                                                                                    'bg-amber-500/20 text-amber-600 dark:text-amber-400'}`}>
                                                                    {status.replace('_', ' ')}
                                                                </span>
                                                                {/* Dock badge */}
                                                                {dockLabel && (
                                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 font-medium flex items-center gap-0.5">
                                                                        <MapPin size={8} /> {dockLabel}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                                                                <span className="truncate max-w-[150px]" title={appt.carrierName}>{appt.carrierName || 'Unknown Carrier'}</span>
                                                                <span>•</span>
                                                                <span className={!appt.driverName ? 'text-amber-500' : ''}>{appt.driverName || 'No Driver'}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Action column */}
                                                    <div className="flex flex-col items-end gap-1 shrink-0">
                                                        {rowAction === 'checkin' && (
                                                            <button
                                                                onClick={() => setPendingConfirm({
                                                                    type: 'checkin',
                                                                    title: 'Confirm Check-In',
                                                                    description: 'This will start the dwell clock and mark the driver as arrived.',
                                                                    confirmLabel: 'Check In',
                                                                    confirmClass: 'bg-blue-600 hover:bg-blue-500 text-white',
                                                                    execute: () => { checkIn(appt); setTimeout(() => focusNextActionableRow(appt.id), 800); },
                                                                })}
                                                                disabled={loading}
                                                                className="px-4 py-2 rounded-lg font-bold text-xs transition-all flex items-center gap-2
                                                                    bg-blue-500/15 text-blue-600 dark:text-blue-400 hover:bg-blue-600 hover:text-white shadow-sm border border-blue-500/30"
                                                            >
                                                                {loading ? (
                                                                    <Loader2 size={14} className="animate-spin" />
                                                                ) : (
                                                                    <Truck size={14} />
                                                                )}
                                                                Check In
                                                            </button>
                                                        )}

                                                        {rowAction === 'dispatch' && (
                                                            <button
                                                                onClick={() => setPendingConfirm({
                                                                    type: 'dispatch',
                                                                    title: 'Confirm Dispatch to Dock',
                                                                    description: `This will mark the truck as actively occupying ${dockLabel ? `Dock ${dockLabel}` : 'the assigned dock'}.`,
                                                                    confirmLabel: 'Dispatch',
                                                                    confirmClass: 'bg-violet-600 hover:bg-violet-500 text-white',
                                                                    execute: () => { dispatchToDock(appt); setTimeout(() => focusNextActionableRow(appt.id), 800); },
                                                                })}
                                                                disabled={loading}
                                                                className="px-4 py-2 rounded-lg font-bold text-xs transition-all flex items-center gap-2
                                                                    bg-violet-500/15 text-violet-600 dark:text-violet-400 hover:bg-violet-600 hover:text-white shadow-sm border border-violet-500/30"
                                                            >
                                                                {loading ? (
                                                                    <Loader2 size={14} className="animate-spin" />
                                                                ) : (
                                                                    <Play size={14} />
                                                                )}
                                                                Dispatch
                                                            </button>
                                                        )}

                                                        {rowAction === 'assign_dock' && (
                                                            <button
                                                                onClick={() => {
                                                                    if (isDockPickerShown) {
                                                                        setDockPickerOpen(null);
                                                                        setSelectedDockId('');
                                                                    } else {
                                                                        setDockPickerOpen(appt.id);
                                                                        setSelectedDockId('');
                                                                    }
                                                                }}
                                                                className="px-4 py-2 rounded-lg font-bold text-xs transition-all flex items-center gap-2
                                                                    bg-amber-500/15 text-amber-600 dark:text-amber-400 hover:bg-amber-500/25 border border-amber-500/30"
                                                            >
                                                                <MapPin size={14} />
                                                                Assign Dock
                                                                <ChevronDown size={12} className={`transition-transform ${isDockPickerShown ? 'rotate-180' : ''}`} />
                                                            </button>
                                                        )}

                                                        {rowAction === 'active' && (
                                                            <span className="px-3 py-1.5 rounded-lg text-xs font-bold bg-violet-500/15 text-violet-600 dark:text-violet-400 border border-violet-500/20 flex items-center gap-1.5">
                                                                <span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" />
                                                                At Dock
                                                            </span>
                                                        )}

                                                        {rowAction === 'done' && (
                                                            <span className="px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-100 dark:bg-slate-700/30 text-slate-400 border border-slate-200 dark:border-slate-700/50 flex items-center gap-1.5">
                                                                <CheckCircle size={12} />
                                                                Done
                                                            </span>
                                                        )}

                                                        {rowAction === 'waiting' && (
                                                            <span className="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-400 dark:text-slate-500">
                                                                Pending
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Inline dock picker (expandable) */}
                                                <AnimatePresence>
                                                    {isDockPickerShown && (
                                                        <motion.div
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: 'auto', opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            transition={{ duration: 0.15 }}
                                                            className="overflow-hidden"
                                                        >
                                                            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-white/5 pl-[76px]">
                                                                <select
                                                                    value={selectedDockId}
                                                                    onChange={(e) => setSelectedDockId(e.target.value)}
                                                                    className="flex-1 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-slate-900 dark:text-white outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                                                                >
                                                                <option value="">Select dock...</option>
                                                                    {(() => {
                                                                        // INV-4.1: Use shared aggregation helper
                                                                        const poMeta = extractPOMetadataForScoring(appt.linkedPOs);
                                                                        const ranked = getRankedDockAssignments(appt, availableDocks, appointments, [], Date.now(), user?.timeZone || 'America/Los_Angeles', poMeta);
                                                                        return ranked.filter(c => c.isEligible).map(c => {
                                                                            // INV-5: Capability label for each dock
                                                                            const dockObj = availableDocks.find((d: any) => d.id === c.dockId);
                                                                            const capLabel = dockObj ? formatDockCapabilities(dockObj) : null;
                                                                            const hint = c.isRecommended
                                                                                ? ' — Best fit'
                                                                                : c.reasons.find(r => r.positive)?.label
                                                                                    ? ` — ${c.reasons.find(r => r.positive)!.label}`
                                                                                    : '';
                                                                            const capPart = capLabel ? ` — ${capLabel}` : '';
                                                                            return (
                                                                                <option key={c.dockId} value={c.dockId}>
                                                                                    Dock {c.dockNumber}{capPart}{hint}
                                                                                </option>
                                                                            );
                                                                        });
                                                                    })()}
                                                                </select>
                                                                <button
                                                                    disabled={!selectedDockId || assigningDock}
                                                                    onClick={() => handleAssignDock(appt.id)}
                                                                    className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white text-xs font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                                                                >
                                                                    {assigningDock ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} />}
                                                                    Assign
                                                                </button>
                                                                <button
                                                                    onClick={() => { setDockPickerOpen(null); setSelectedDockId(''); }}
                                                                    className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 px-2 py-2"
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Late check-in time confirmation */}
            <LateTimeModal {...lateModalProps} />

            {/* Unified Confirmation AlertDialog */}
            <AlertDialog open={!!pendingConfirm} onOpenChange={(open) => { if (!open) setPendingConfirm(null); }}>
              <AlertDialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-slate-900 dark:text-slate-100">{pendingConfirm?.title}</AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-600 dark:text-slate-400">{pendingConfirm?.description}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="text-slate-700 dark:text-slate-300">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className={pendingConfirm?.confirmClass || 'bg-blue-600 text-white'}
                    onClick={() => {
                      pendingConfirm?.execute();
                      setPendingConfirm(null);
                    }}
                  >
                    {pendingConfirm?.confirmLabel}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
        </AnimatePresence>
    );
}
